import axios from 'axios';

import { DataCache } from '../../../components/data-driven-logo/data-cache';
import { fixupAllCarriersWithSvgsReferenceData, getCarriersFromIds } from '../../../store/reference-data/reference-data-fixup-helpers';
import { CountryService } from '../../../services/countryService';
import { mergeLabelProviders } from '../../../store/reference-data/reference-data.epics';
import { LogService } from '../../../services/log-service';
import { rebrandCarrierProviderNames } from '../../../components/data-driven-logo/name-utils';

const getCountries = () => {
  const localStorageCountries = CountryService.getCountriesFromLocalStorage();
  if (!localStorageCountries) {
    return axios.get('/reference/country');
  }
  return new Promise((resolve) => resolve({ data: localStorageCountries }));
};

const getStates = () => {
  const localStorageStates = CountryService.getStatesFromLocalStorage();
  if (!localStorageStates) {
    return axios.get('/reference/state');
  }
  return new Promise((resolve) => resolve({ data: localStorageStates }));
};

export const phase3Call = (data) => {
  // oxlint-disable-next-line no-async-promise-executor
  return new Promise(async (resolve, reject) => {
    try {
      const { homeCountry, subscriptionPlanInfo } = data.input.seller;
      const { isVendorOr3PL } = subscriptionPlanInfo;

      const promises = [
        axios.get('/provider/seller'),
        axios.get('/carrier/seller/carrierIds'),
        axios.get('/carrier'),
        getCountries(),
        getStates(),
        axios.get('/service/classifications'),
        axios.get('/IntegrationsPlatform/displayInfo/providers'),
        axios.get('/IntegrationsPlatform/displayInfo/ordersources'),
        axios.get('/IntegrationsPlatform/displayInfo/fulfillmentproviders'),
        axios.get('/reference/service'),
        axios.get('/freight/provider').catch(() => ({ data: [] })),
      ];

      if (isVendorOr3PL) {
        promises.push(axios.get('/carriersharing/partner/carrierids'));
        promises.push(axios.get('/carriersharing/partner/provider/seller'));
      }

      const promiseData = await Promise.all(promises);
      const errors = promiseData.filter((response) => response?.status && response.status !== 200).map((response) => response.statusText);
      if (errors.length > 0) {
        throw new Error(errors.join(','));
      }
      const [
        labelProvidersResponse,
        sellerCarrierIdsResponse,
        allTheCarriersResponse,
        countriesResponse,
        statesResponse,
        classificationsResponse,
        providerDisplayInfoResponse,
        marketplaceDisplayInfoResponse,
        fulfillmentProviderDisplayInfoResponse,
        serviceReferenceResponse,
        freightProvidersResponse,
        partnerSharedCarrierIdsResponse,
        partnerSharedSellerLabelProvidersResponse,
      ] = promiseData;

      const labelProvidersData = rebrandCarrierProviderNames(labelProvidersResponse?.data || [], homeCountry);
      const providerDisplayInfoData = rebrandCarrierProviderNames(providerDisplayInfoResponse?.data || [], homeCountry);

      // Connected SellerFreightProvider list is hydrated separately in phase2.
      // Normalize `code` → `apiCode` (lowercase) so CUI's cache lookup and reference-bags resolve correctly.
      const rawFreightProviders = Array.isArray(freightProvidersResponse?.data)
        ? freightProvidersResponse.data
        : (freightProvidersResponse?.data?.providers ?? []);
      const freightProviders = rawFreightProviders.map((p) => ({ ...p, apiCode: p.code?.toLowerCase() ?? p.apiCode }));

      DataCache.setCache({
        labelProviders: labelProvidersData,
        providers: providerDisplayInfoData,
        marketplaces: marketplaceDisplayInfoResponse?.data || [],
        fillProviders: fulfillmentProviderDisplayInfoResponse?.data || [],
        freightProviders,
      });

      const carrierIds = [...new Set([...(sellerCarrierIdsResponse?.data || []), ...(partnerSharedCarrierIdsResponse?.data || [])])]; // this is to make sure values are distinct
      let allSupportedCarrierIdsAndTheirConfirmations = allTheCarriersResponse?.data;
      if (!Array.isArray(allSupportedCarrierIdsAndTheirConfirmations)) {
        LogService.logError(new Error('Error parsing carriers response.'), { rawResponse: allTheCarriersResponse?.data });
        allSupportedCarrierIdsAndTheirConfirmations = [];
      }
      const allTheCarriers = fixupAllCarriersWithSvgsReferenceData(allSupportedCarrierIdsAndTheirConfirmations);
      const carriers = getCarriersFromIds(carrierIds, allTheCarriers);
      const labelProviders = mergeLabelProviders(labelProvidersData, partnerSharedSellerLabelProvidersResponse?.data || []);
      const countries = countriesResponse?.data || [];
      CountryService.writeCountriesToLocalStorage(countries);
      const states = statesResponse?.data || [];
      CountryService.writeStatesToLocalStorage(states);

      const newData = {
        referenceData: {
          marketplaces: marketplaceDisplayInfoResponse?.data || [],
          labelProviders,
          carriers,
          allTheCarriers,
          countries,
          states,
          serviceClassifications: classificationsResponse?.data || [],
          fulfillmentProviders: fulfillmentProviderDisplayInfoResponse?.data || [],
          serviceReference: serviceReferenceResponse?.data || [],
          freightProviders,
        },
      };

      resolve(newData);
    } catch (e) {
      reject(e);
    }
  });
};
